/**
 * A site's Overview tab (JSS#1157): Site Info, Ready for JBS, Boot Images,
 * Platform Prerequisites and, behind the `netboot` flag, Network Boot and
 * Pending Boots.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Activity, Download, ExternalLink, Globe, Lock, Unlock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { sitesApi, clusterApi } from '@/api/client';
import { parseApiError } from '@/lib/apiError';
import NetworkBootCard from '@/components/deployments/NetworkBootCard';
import PendingBootsPanel from '@/components/deployments/PendingBootsPanel';
import { SiteReadinessPanel } from '@/components/deployments/SiteReadinessPanel';
import { PrerequisitesCard } from '@/components/deployments/PrerequisitesCard';
import { siteWebHost, siteWebUrl } from '@/lib/siteUrls';
import type { Site } from '@/types';
import { BootImagesCard } from './BootImagesCard';
import { InfoRow, JbsStatusBadge, siteClusterInstalled, useSuperUser } from './shared';

/** The JBS LAN and NMOS registry URLs on a site's LoadBalancer Services. */
export interface JbsAccessUrls {
  jbsFrontendIp: string | null;
  jbsFrontendUrl: string | null;
  jbsNmosUrl: string | null;
}

/**
 * Query JBS LoadBalancer IPs for the overview. SiteDetail calls this, not
 * SiteOverview, so the query keeps polling whichever tab is open, as it did
 * before the split.
 */
export function useJbsAccessUrls(siteId: string, site: Site | undefined): JbsAccessUrls {
  const { data: jbsServices } = useQuery({
    queryKey: ['cluster-services', siteId, 'jbs'],
    queryFn: () => clusterApi.getServices(siteId, 'jbs'),
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: !!siteId && !!site && siteClusterInstalled(site.status),
  });
  const jbsFrontendSvc = jbsServices?.find((s: any) => s.name?.includes('frontend') && s.type === 'LoadBalancer' && s.externalIP);
  const jbsFrontendIp = jbsFrontendSvc?.externalIP ?? null;
  const jbsFrontendPort = jbsFrontendSvc?.ports?.[0]?.port;
  const jbsFrontendUrl = jbsFrontendIp ? `https://${jbsFrontendIp}${jbsFrontendPort && jbsFrontendPort !== 443 ? `:${jbsFrontendPort}` : ''}` : null;
  // Per-medianode NMOS interfaces (livesource-…-st2110-ingress-nmos-external
  // / -egress-N-nmos-external) live alongside the registry and used to win
  // this lookup, which sent operators to a 404 or to the wrong service on
  // both JBS clusters. Match the registry by exact name; fall back to its
  // canonical http port so a name-rename upstream doesn't silently break
  // this again.
  const jbsNmosSvc = jbsServices?.find((s: any) =>
    s.name === 'nmos-registry-external' && s.type === 'LoadBalancer' && s.externalIP,
  );
  const jbsNmosIp = jbsNmosSvc?.externalIP ?? null;
  const jbsNmosHttpPort = jbsNmosSvc?.ports?.find((p: any) => p.name === 'http')?.port ?? jbsNmosSvc?.ports?.[0]?.port;
  const jbsNmosUrl = jbsNmosIp ? `http://${jbsNmosIp}${jbsNmosHttpPort && jbsNmosHttpPort !== 80 ? `:${jbsNmosHttpPort}` : ''}` : null;
  return { jbsFrontendIp, jbsFrontendUrl, jbsNmosUrl };
}

/**
 * The Site Info "Security" row: the site's lockdown state for everyone, and
 * the switch for a platform superuser only (JSS#993).
 *
 * Out of lockdown ("development mode") the site's talosconfig and a
 * system:masters kubeconfig can be downloaded, and the next config render
 * drops Talos API RBAC and PodSecurity enforcement. So it is Jeket's
 * call, never a persona role's, Admin included: `PUT /api/sites/:id/lockdown`
 * is superuser-only at the BFF and in deployment-service, matching this gate.
 * The switch used to send PATCH /sites/:id, whose SiteOperator gate refused a
 * superuser with no persona role in the acting org.
 */
export function SiteLockdownControl({ siteId, lockdown }: { siteId: string; lockdown: boolean }) {
  const queryClient = useQueryClient();
  const isSuperUser = useSuperUser();
  const toggle = useMutation({
    mutationFn: (next: boolean) => sitesApi.setLockdown(siteId, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['site', siteId] }),
  });

  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-xs text-silver-muted shrink-0">Security</span>
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <span className={cn(
            'flex items-center gap-1.5 text-xs font-medium',
            lockdown ? 'text-status-healthy' : 'text-status-warning'
          )}>
            {lockdown ? <Lock size={12} /> : <Unlock size={12} />}
            {lockdown ? 'Locked down' : 'Development mode'}
          </span>
          {isSuperUser && (
            <button
              type="button"
              role="switch"
              aria-checked={lockdown}
              aria-label="Lockdown"
              onClick={() => toggle.mutate(!lockdown)}
              disabled={toggle.isPending}
              className={cn(
                'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none',
                lockdown ? 'bg-status-healthy/40' : 'bg-silver-muted/30'
              )}
            >
              <span className={cn(
                'inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform',
                lockdown ? 'translate-x-[18px]' : 'translate-x-[2px]'
              )} />
            </button>
          )}
        </div>
        {toggle.isError && (
          <span role="alert" className="text-[10px] text-status-error">
            {parseApiError(toggle.error).message}
          </span>
        )}
      </div>
    </div>
  );
}

export function SiteOverview({ siteId, site, access }: {
  siteId: string;
  site: Site;
  access: JbsAccessUrls;
}) {
  const { jbsFrontendIp, jbsFrontendUrl, jbsNmosUrl } = access;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Site Info */}
      <div className="bg-surface border border-surface-elevated rounded-xl p-5">
        <h3 className="text-sm font-semibold text-silver-bright mb-4">Site Info</h3>
        {site.description && (
          <p className="text-sm text-silver-muted mb-4">{site.description}</p>
        )}
        <div className="space-y-3">
          <InfoRow label="Cluster" value={site.clusterName} mono />
          <InfoRow label="Talos Version" value={site.talosVersion} mono />
          <InfoRow label="K8s Version" value={site.kubernetesVersion} mono />
          <InfoRow
            label="CP Nodes"
            value={`${site.config.controlPlaneCount ?? 1} control plane node${(site.config.controlPlaneCount ?? 1) !== 1 ? 's' : ''}`}
          />
          {site.config.controlPlaneVip && (
            <InfoRow label="Control Plane VIP" value={site.config.controlPlaneVip} mono />
          )}
          <SiteLockdownControl siteId={siteId} lockdown={site.lockdown} />
          <InfoRow label="Created" value={new Date(site.createdAt).toLocaleString()} />
          {/* JBS install status (#164) — separate from `status` which
              only tracks platform prerequisites. */}
          <div className="flex items-start justify-between gap-4">
            <span className="text-xs text-silver-muted shrink-0">JBS</span>
            <JbsStatusBadge
              status={site.jbsStatus}
              message={site.jbsStatusMessage}
            />
          </div>
          {/* Access Links */}
          {site.slug && (
            <div className="flex items-start justify-between gap-4">
              <span className="text-xs text-silver-muted shrink-0">Web Access</span>
              {site.jbsStatus === 'installed' ? (
                <a
                  href={siteWebUrl(site)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-light transition-colors font-mono"
                >
                  <Globe size={12} />
                  {siteWebHost(site)}
                  <ExternalLink size={10} />
                </a>
              ) : (
                <span
                  className="flex items-center gap-1.5 text-xs text-silver-muted font-mono cursor-not-allowed"
                  title="JBS isn't installed on this site yet — install it to enable the public URL"
                >
                  <Globe size={12} />
                  {siteWebHost(site)}
                </span>
              )}
            </div>
          )}
          {jbsFrontendUrl && (
            <div className="flex items-start justify-between gap-4">
              <span className="text-xs text-silver-muted shrink-0">LAN Access</span>
              <a
                href={jbsFrontendUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-light transition-colors font-mono"
              >
                <Globe size={12} />
                {jbsFrontendUrl.replace('https://', '')}
                <ExternalLink size={10} />
              </a>
            </div>
          )}
          {jbsNmosUrl && (
            <div className="flex items-start justify-between gap-4">
              <span className="text-xs text-silver-muted shrink-0">NMOS Registry</span>
              <a
                href={jbsNmosUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-light transition-colors font-mono"
              >
                <Activity size={12} />
                {jbsNmosUrl?.replace(/^https?:\/\//, '')}
                <ExternalLink size={10} />
              </a>
            </div>
          )}
          {!jbsFrontendIp && site.proxyAddress && (
            <div className="flex items-start justify-between gap-4">
              <span className="text-xs text-silver-muted shrink-0">Node IP</span>
              <span className="text-xs text-silver-bright font-mono">{site.proxyAddress}</span>
            </div>
          )}
          {!site.lockdown && (
            <div className="flex items-start justify-between gap-4">
              <span className="text-xs text-silver-muted shrink-0">Cluster Access</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    try {
                      const blob = await sitesApi.downloadFile(site.id, 'talosconfig');
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = 'talosconfig'; a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      console.error('Failed to download talosconfig:', e);
                    }
                  }}
                  className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-light transition-colors"
                >
                  <Download size={12} />
                  talosconfig
                </button>
                <button
                  onClick={async () => {
                    try {
                      const blob = await sitesApi.downloadFile(site.id, 'kubeconfig');
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = 'kubeconfig'; a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      console.error('Failed to download kubeconfig:', e);
                    }
                  }}
                  className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-light transition-colors"
                >
                  <Download size={12} />
                  kubeconfig
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ready for JBS (#796/#804): every readiness check, with its
          remediation and docs link; the JBS install is refused until
          they all pass. */}
      <SiteReadinessPanel siteId={site.id} />

      <BootImagesCard site={site} />

      {/* Platform Prerequisites: the Helm steps, or the operator's
          handoff and components on an operator-path site (#805). */}
      <PrerequisitesCard site={site} />

      {/* Network Boot — Phase 5a/b HTTPS boot URL + per-token wizard.
          Behind the `netboot` feature flag, off by default (#954): the
          card renders nothing while it is off for this site. */}
      <NetworkBootCard siteId={site.id} />

      {/* Pending Boots — per-site filtered list (#398). Renders nothing
          when no approvals are waiting for this site, or while the
          `netboot` flag is off (#954). */}
      <PendingBootsPanel siteId={site.id} />

    </div>
  );
}
